import { useMemo } from 'react';
import {
  type AssignmentDto,
  dayFromLocalDate,
  daysInMonth,
  localDateFromDay,
  monthGridDayRange,
} from '@betreuung/shared';
import type { PersonLookup } from '@/lib/persons';
import { de } from '@/i18n/de';
import { DayCell } from './DayCell';
import { type MonthSelection, daySelectionRole } from './useMonthSelection';

export interface MonthGridProps {
  year: number;
  month: number;
  assignments: readonly AssignmentDto[];
  persons: PersonLookup;
  bandHeight: number;
  todayDay: number;
  selection: MonthSelection;
  onDayTap: (dayIndex: number) => void;
}

interface GridDay {
  dayIndex: number;
  dayOfMonth: number;
  inMonth: boolean;
}

/**
 * Monday-to-Sunday weeks of the visible month, as one grid.
 *
 * It used to be a grid per week, because the inline day detail opened *between*
 * two week rows and pushed the following ones down. That view is gone — a tap
 * opens the day's sheet directly — so the rows have nothing left to separate.
 */
export function MonthGrid({
  year,
  month,
  assignments,
  persons,
  bandHeight,
  todayDay,
  selection,
  onDayTap,
}: MonthGridProps) {
  const days = useMemo(() => buildDays(year, month), [year, month]);

  return (
    <div className="grid grid-cols-7 gap-1 px-3 pb-1 nav:gap-[5px] nav:px-0 nav:pb-[5px]">
      {de.month.weekdays.map((label) => (
        <span key={label} className="text-center font-mono text-meta font-medium text-fg-hint">
          {label}
        </span>
      ))}

      {days.map((day) => (
        <DayCell
          key={day.dayIndex}
          dayIndex={day.dayIndex}
          dayOfMonth={day.dayOfMonth}
          assignments={assignments}
          persons={persons}
          bandHeight={bandHeight}
          inMonth={day.inMonth}
          isToday={day.dayIndex === todayDay}
          role={daySelectionRole(selection, day.dayIndex)}
          onTap={onDayTap}
        />
      ))}
    </div>
  );
}

function buildDays(year: number, month: number): GridDay[] {
  const { firstDay, lastDay } = monthGridDayRange(year, month);
  const monthFirstDay = dayFromLocalDate(year, month, 1);
  const monthLastDay = dayFromLocalDate(year, month, daysInMonth(year, month));

  const days: GridDay[] = [];
  for (let dayIndex = firstDay; dayIndex <= lastDay; dayIndex += 1) {
    days.push({
      dayIndex,
      dayOfMonth: localDateFromDay(dayIndex).day,
      inMonth: dayIndex >= monthFirstDay && dayIndex <= monthLastDay,
    });
  }
  return days;
}
